import { Category1Types } from 'enums/category1Enums';
import React, { FC, useEffect } from 'react';
import { getFormData } from './category1LayoutData';
import FieldsRow from 'shared/components/FieldsRow';
import { FormType } from 'enums/FieldTypeEnums';
import { Col, Row } from 'antd';
import { Origins } from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import {
  useFormContext,
  useFieldArray,
  UseFieldArrayRemove,
} from 'react-hook-form';
import OriginsLayout from './originsLayout';
import { RawMaterialIngredientsMeta } from 'models/RawMaterialIngredientsMeta/rawMaterialIngredientsMeta.model';
import { CategoryOptions } from 'shared/components/CreateProductModal/ProductModalForm';
import { useState } from 'react';
import RequestIngredientModal from 'shared/components/RequestIngredientModal';
import RequestProductionRegionModal from 'shared/components/RequestProductionRegion';
import { categoryTypeFieldMap } from '../Category4&9/Category4Layout/category4LayoutData';
import DashboardService from 'services/DashboardService/dashboard.service';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';
import { useScopeThreeFormEdit } from 'context/ScopeThreeFormEditContext';

export interface Catgeory1LayoutProps {
  type: Category1Types;
  index: number;
  remove?: UseFieldArrayRemove;
  ingredientsMeta?: RawMaterialIngredientsMeta[];
  rawMateialPackagingMeta?: RawMaterialIngredientsMeta[];
  setActiveTabKey?: React.Dispatch<React.SetStateAction<string | string[]>>;
}

const Catgeory1Layout: FC<Catgeory1LayoutProps> = ({
  type,
  index,
  remove,
  ingredientsMeta,
  setActiveTabKey,
}) => {
  const { isEditView } = useScopeThreeFormEdit();
  const readOnly = !isEditView;
  const { bestFitRegion, fetchIngredientOrigin, resetBestFitRegion, loading } =
    Stage1AndStage2ProductService();
  const { fetchActivityMapping, activityMapping } = DashboardService();
  const { control, watch, setValue, trigger } = useFormContext();
  const [isAddIngredient, setIsAddIngredient] = useState<boolean>(false);
  const [showBestFitRegionModal, setShowBestFitRegionModal] =
    useState<boolean>(false);
  const typeValue = categoryTypeFieldMap[type];
  const ingreditentName = watch(`${typeValue}.${index}.activityLabel`);

  useEffect(
    () => {
      if (!ingreditentName) {
        resetBestFitRegion();
        return;
      }
      fetchActivityMapping({
        activity_label: ingreditentName,
      });
      fetchIngredientOrigin({
        activity_label: ingreditentName,
      });
      fetchActivityMapping({
        activity_label: ingreditentName,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activity label driven; service fns recreated every render
    [ingreditentName]
  );

  const {
    fields: originFields,
    append: appendOrigin,
    remove: removeOrigin,
  } = useFieldArray({
    control,
    name: `${typeValue}.${index}.origins`,
  });
  const ingredients = watch(`${typeValue}`);
  const origins = watch(`${typeValue}.${index}.origins`);

  const handleIngredientChange = async () => {
    const activeIngredient = watch(`${typeValue}.${index}.activityLabel`);
    setValue?.(`${typeValue}.${index}.bestFitRegion`, null);
    fetchActivityMapping({
      activity_label: activeIngredient,
    });
    await trigger(`${typeValue}`);
    setValue(
      `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`,
      null
    );
    setValue(`${typeValue}.${index}.origins.0.addressLabel`, null);
    origins.forEach((_: Origins, originIndex: number) => {
      setValue(
        `${typeValue}.${index}.origins.${originIndex}.activityMappingRegionSpecId`,
        null
      );
      setValue(
        `${typeValue}.${index}.origins.${originIndex}.addressLabel`,
        null
      );
      setValue(`${typeValue}.${index}.origins.${originIndex}.address`, null);
      setValue(`${typeValue}.${index}.origins.${originIndex}.map`, null);
    });
  };

  const addOrigin = async () => {
    if (readOnly) return;
    if (watch(`${typeValue}.${index}.origins`).length === 1) {
      setValue?.(`${typeValue}.${index}.origins.0.share`, null);
      setValue?.(
        `${typeValue}.${index}.origins`,
        watch(`${typeValue}.${index}.origins`)
      );
      setValue?.(`${typeValue}.${index}.bestFitRegion`, null);
      await trigger(`${typeValue}.${index}.bestFitRegion`);
    }
    appendOrigin?.(new Origins());
    setValue?.(`${typeValue}.${index}.bestFitRegion`, null);
    setValue(
      `${typeValue}.${index}.origins`,
      watch(`${typeValue}.${index}.origins`)
    );
    setValue(typeValue, watch(`${typeValue}`));
  };

  const deleteOrigin = async (originInd?: number) => {
    if (readOnly) return;
    removeOrigin(originInd);
    if (watch(`${typeValue}.${index}.origins`)?.length === 1) {
      setValue?.(`${typeValue}.${index}.origins.0.share`, null);
      setValue(
        `${typeValue}.${index}.origins.0.addressLabel`,
        watch(`${typeValue}.${index}.origins.0.addressLabel`)
      );
      setValue(
        `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`,
        watch(`${typeValue}.${index}.origins.0.activityMappingRegionSpecId`)
      );
      await trigger(`${typeValue}.${index}.origins.0.addressLabel`);
      await trigger(
        `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`
      );
    }
    setValue(
      `${typeValue}.${index}.origins`,
      watch(`${typeValue}.${index}.origins`)
    );
    setValue(
      `${typeValue}.${index}.origins.0.addressLabel`,
      watch(`${typeValue}.${index}.origins.0.addressLabel`),
      { shouldValidate: true }
    );
    await trigger(`${typeValue}.${index}.origins.0.addressLabel`);
    setValue(
      `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`,
      watch(`${typeValue}.${index}.origins.0.activityMappingRegionSpecId`)
    );
    await trigger(
      `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`
    );
  };
  const formData = getFormData({
    index,
    type,
    originsLength: watch(`${typeValue}.${index}.origins`)?.length,
    ingredientsLength: ingredients?.length,
    remove,
    appendOrigin: addOrigin,
    nameOptions: ingredientsMeta as CategoryOptions[],
    setIsAddIngredient,
    setIsAddRegion: setShowBestFitRegionModal,
    bestFitRegionOptions: bestFitRegion,
    setValue,
    handleChange: handleIngredientChange,
    setActiveTabKey,
    loading,
    readOnly,
  });
  const handleCloseRequestIngredientModal = () => setIsAddIngredient(false);
  const toggleBestFitRegion = () => setShowBestFitRegionModal(false);

  const ingredientId: string =
    activityMapping?.length > 0 ? activityMapping[0]?.id?.toString() ?? '' : '';

  return (
    <Row>
      <Col span={24}>
        <FieldsRow {...formData} formType={FormType.REACT_HOOK_FORMS} />
      </Col>
      <Col span={24}>
        {originFields?.map((origin, originIndex: number) => {
          if (origins?.length === 1) return null;
          return (
            <OriginsLayout
              key={origin.id}
              index={index}
              removeOrigin={deleteOrigin}
              type={type}
              originIndex={originIndex}
              bestFitRegion={bestFitRegion}
              setValue={setValue}
              setIsAddRegion={setShowBestFitRegionModal}
              loading={loading}
              originsLength={origins?.length}
              readOnly={readOnly}
            />
          );
        })}
      </Col>
      <RequestIngredientModal
        showModalState={isAddIngredient}
        handleModalCancel={handleCloseRequestIngredientModal}
        handleModalConfirm={handleCloseRequestIngredientModal}
        isPackageMaterial={type === Category1Types.PACKAGING_MATERIALS}
        isOtherMaterial={type === Category1Types.OTHERS}
        isScopeThree
      />
      <RequestProductionRegionModal
        showModalState={showBestFitRegionModal}
        handleModalCancel={toggleBestFitRegion}
        handleModalConfirm={toggleBestFitRegion}
        ingredientName={ingreditentName ?? ''}
        ingredientId={ingredientId}
        isScopeThree
      />
    </Row>
  );
};

export default Catgeory1Layout;
